import {
  addMinutes,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  differenceInWeeks,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isToday,
  isTomorrow,
} from 'date-fns';
import { DateTime, Settings } from 'luxon';

export const monthDayTimeFormat = (value: string) => {
  const date = new Date(value);
  const dateWithoutTimezoneOffset = getDateWithoutTimezoneOffset(value);

  if (isToday(dateWithoutTimezoneOffset)) {
    return `Today ${formatWithoutTimezoneOffset(date, 'HH:mm')}`;
  } else if (isTomorrow(dateWithoutTimezoneOffset)) {
    return `Tomorrow ${formatWithoutTimezoneOffset(date, 'HH:mm')}`;
  }
  return formatWithoutTimezoneOffset(new Date(value), 'MM/dd HH:mm');
};

export const timeFormat = (value: string, firstDate: string) => {
  return isSameDay(new Date(value), new Date(firstDate))
    ? formatWithoutTimezoneOffset(new Date(value), 'HH:mm')
    : monthDayTimeFormat(value);
};

export const getDateWithoutTimezoneOffset = (value: string) => {
  const date = new Date(value);
  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() + userTimezoneOffset);
};

export const formatWithoutTimezoneOffset = (
  date: Date,
  stringFormat: string
) => {
  var userTimezoneOffset = date.getTimezoneOffset() * 60000;
  const dateWithoutOffset = new Date(date.getTime() + userTimezoneOffset);
  return format(dateWithoutOffset, stringFormat);
};

export const getTimeSinceDate = (dateString: string) => {
  const now = new Date();
  const date = new Date(dateString);

  const weeks = differenceInWeeks(now, date);
  if (weeks > 0) {
    return `${weeks}w`;
  }

  const days = differenceInDays(now, date);
  if (days > 0) {
    return `${days}d`;
  }

  const hours = differenceInHours(now, date);
  if (hours > 0) {
    return `${hours}h`;
  }

  const minutes = differenceInMinutes(now, date);
  if (minutes > 0) {
    return `${minutes}m`;
  }

  const seconds = differenceInSeconds(now, date);
  if (seconds > 0) {
    return `${seconds}s`;
  }

  return '';
};

export const isPastExpirationTime = (
  expirationTimer: number,
  value: string | undefined
) => {
  if (!value) {
    return false;
  }
  const date = new Date(value);
  const now = new Date();

  return isAfter(now, addMinutes(date, expirationTimer));
};

export const calculateTimeLeft = (createdAt: string, minutes: number) => {
  const createdAtDate = new Date(createdAt);
  const minutesAfter = addMinutes(createdAtDate, minutes);
  const difference = +minutesAfter - +new Date();

  if (difference > 0) {
    const minutes = Math.floor((difference % 3.6e6) / 6e4);
    const seconds = Math.floor((difference % 6e4) / 1000);

    return {
      minutes: minutes < 10 ? `0${minutes}` : `${minutes}`,
      seconds: seconds < 10 ? `0${seconds}` : `${seconds}`,
    };
  }
  return { minutes: '00', seconds: '00' };
};

export const formatMonthDate = (value: string) => {
  return formatWithoutTimezoneOffset(new Date(value), 'MM/dd ');
};

export const formatDate = (date: Date) =>
  formatWithooutTimezoneOffset(date, 'MM/dd/yyyy');

// we dont want to adjust timezone so reset the offset and format
const formatWithooutTimezoneOffset = (date: Date, stringFormat: string) => {
  if (!isValidDate(date)) return '';

  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  const dateWithoutOffset = new Date(date.getTime() + userTimezoneOffset);
  return format(dateWithoutOffset, stringFormat);
};

const isValidDate = (date: Date) => {
  if (Object.prototype.toString.call(date) === '[object Date]') {
    // it is a date
    if (isNaN(date.getTime())) {
      // d.valueOf() could also work
      // date is not valid
      return false;
    } else {
      // date is valid
      return true;
    }
  } else {
    // not a date
    return false;
  }
};

export const dateTimeIsValid = (
  requiredPickupDateTime: Date,
  newTime: string,
  emptyDateTime?: boolean
): boolean => {
  const userSelectedDateTime = new Date(newTime);
  if (emptyDateTime) {
    if (
      differenceInMinutes(requiredPickupDateTime, userSelectedDateTime) < 60 ||
      isAfter(userSelectedDateTime, requiredPickupDateTime)
    ) {
      return false;
    }
  } else if (isAfter(userSelectedDateTime, requiredPickupDateTime)) {
    return false;
  }
  return true;
};

export const isDateTimeInTheFuture = (value: string): boolean => {
  return isAfter(new Date(value), new Date());
};

export const isDateTimeBefore = (selectedDate: string, compareDate: string) => {
  return isBefore(new Date(selectedDate), new Date(compareDate));
};

export const areDatesSameDay = (date1: Date, date2: Date) => {
  return isSameDay(date1, date2);
};

export const getUserTimeZone = (): string => {
  const localDateTime = DateTime.local();
  return localDateTime.zoneName;
};
export const setLuxonDefaultZoneToUserTimeZone = () => {
  Settings.defaultZone = getUserTimeZone();
};
